import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Dropdown } from "@faire/web--source/slate/Dropdown";
import { InputWrapper } from "@faire/web--source/slate/Input";
import { MeasurementInput } from "@faire/web--source/slate/Input/MeasurementInput";
import { LoadingSpinner } from "@faire/web--source/slate/LoadingSpinner";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { trackRetailersRetailerSelectCustomOptionClick } from "@faire/web-api--source/events/retailers/click/retailerSelectCustomOption";
import { trackRetailersSelectQuantityDropdownClick } from "@faire/web-api--source/events/retailers/click/selectQuantityDropdown";
import { useState, useEffect, useMemo, useCallback } from "react";
import * as React from "react";
import styled, { css } from "styled-components";
import { NUMBER_OF_QUANTITY_OPTIONS } from "@faire/retailer-visitor-shared/views/product/ProductDetails/AddToCart/useProductAddToCartState";
var MINIMUM_INPUT_WIDTH = 75;
var SMALL_MIN_INPUT_WIDTH = 50;
var HORIZONTAL_PADDING = 16;
export var QuantityOrOptionPicker = function(_param) {
    var value = _param.value, options = _param.options, className = _param.className, quantityTextAlign = _param.quantityTextAlign, dynamicWidth = _param.dynamicWidth, shouldUseCustomOption = _param.shouldUseCustomOption, getCustomOptionLabel = _param.getCustomOptionLabel, small = _param.small, props = _object_without_properties(_param, [
        "value",
        "options",
        "className",
        "quantityTextAlign",
        "dynamicWidth",
        "shouldUseCustomOption",
        "getCustomOptionLabel",
        "small"
    ]);
    var isTabletAndBelow = useViewport().isTabletAndBelow;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var _useState = _sliced_to_array(useState(false), 2), hasEditedCustomOption = _useState[0], setHasEditedCustomOption = _useState[1];
    var _useState1 = _sliced_to_array(useState({
        value: "",
        label: strictLocalize({
            id: "dhKCUV",
            defaultMessage: [
                {
                    type: 0,
                    value: "Custom Quantity"
                }
            ]
        })
    }), 2), customOption = _useState1[0], setCustomOption = _useState1[1];
    var isValueInOptions = useCallback(function(value) {
        var useAllOptions = options.length < NUMBER_OF_QUANTITY_OPTIONS;
        var _options = useAllOptions ? options : options.slice(0, -1);
        return !!_options.find(function(option) {
            return option.value === value || value === customOption.value;
        });
    }, [
        customOption.value,
        options
    ]);
    useEffect(function() {
        if (!isValueInOptions(value) && shouldUseCustomOption) {
            var _getCustomOptionLabel;
            setCustomOption({
                value: value,
                label: (_getCustomOptionLabel = getCustomOptionLabel === null || getCustomOptionLabel === void 0 ? void 0 : getCustomOptionLabel(parseInt(value))) !== null && _getCustomOptionLabel !== void 0 ? _getCustomOptionLabel : ""
            });
            setHasEditedCustomOption(true);
        }
    }, [
        getCustomOptionLabel,
        isValueInOptions,
        shouldUseCustomOption,
        value
    ]);
    var quantityPickerWidth = useMemo(function() {
        var minWidth = small ? SMALL_MIN_INPUT_WIDTH : MINIMUM_INPUT_WIDTH;
        var currentWidth = value === undefined ? minWidth : value.toString().length * 10 + 3 * HORIZONTAL_PADDING;
        return Math.max(currentWidth, minWidth);
    }, [
        small,
        value
    ]);
    var quantityAlignRight = quantityTextAlign === "left" && isTabletAndBelow;
    return /*#__PURE__*/ _jsx(Wrapper, {
        className: className,
        $dynamicWidth: dynamicWidth,
        $width: quantityPickerWidth + "px",
        $quantityAlignRight: quantityAlignRight,
        $minWidth: small ? SMALL_MIN_INPUT_WIDTH : MINIMUM_INPUT_WIDTH,
        children: /*#__PURE__*/ _jsx(QuantityDropdown, _object_spread_props(_object_spread({}, props), {
            value: value,
            options: options,
            quantityTextAlign: quantityTextAlign,
            shouldUseCustomOption: shouldUseCustomOption,
            small: small,
            hasEditedCustomOption: hasEditedCustomOption,
            customOption: customOption
        }))
    });
};
var QuantityDropdown = function(param) {
    var value = param.value, placeholder = param.placeholder, options = param.options, onSelect = param.onSelect, loading = param.loading, disabled = param.disabled, containingElement = param.containingElement, dataTestId = param.dataTestId, quantityTextAlign = param.quantityTextAlign, shouldUseCustomOption = param.shouldUseCustomOption, small = param.small, hasEditedCustomOption = param.hasEditedCustomOption, customOption = param.customOption;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var _useState = _sliced_to_array(useState(false), 2), customQuantitySelection = _useState[0], setCustomQuantitySelection = _useState[1];
    var handleOnCustomOptionChange = function(value) {
        trackRetailersRetailerSelectCustomOptionClick(value);
        handleChangeValue(value);
    };
    var handleDropdownSelect = function(value) {
        trackRetailersSelectQuantityDropdownClick(value);
        handleChangeValue(value);
    };
    var handleChangeValue = function(value) {
        var _options_;
        var _options__value;
        var lastOptionValue = (_options__value = (_options_ = options[options.length - 1]) === null || _options_ === void 0 ? void 0 : _options_.value) !== null && _options__value !== void 0 ? _options__value : "";
        var newValue = value === undefined ? lastOptionValue : String(value);
        if (customQuantitySelection) {
            setCustomQuantitySelection(false);
        } else if (value === lastOptionValue) {
            setCustomQuantitySelection(true);
        }
        onSelect({
            value: newValue
        });
    };
    var hideCustomOptionSelector = options.length < NUMBER_OF_QUANTITY_OPTIONS;
    if (shouldUseCustomOption && !disabled) {
        return /*#__PURE__*/ _jsx(StyledCustomDropdown, {
            value: value,
            "data-test-id": dataTestId,
            options: hideCustomOptionSelector ? options : options.slice(0, -1),
            onSelect: function(param) {
                var value = param.value;
                return handleDropdownSelect(value);
            },
            placeholder: !hideCustomOptionSelector ? strictLocalize({
                id: "bwnOM9",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Enter Custom Quantity"
                    }
                ]
            }) : undefined,
            containingElement: containingElement,
            customOption: !hideCustomOptionSelector ? _object_spread_props(_object_spread({}, customOption), {
                onValueChange: handleOnCustomOptionChange,
                hasEdited: hasEditedCustomOption
            }) : undefined,
            useTabletAndAboveDropdownStyle: true
        });
    } else if (loading) {
        return /*#__PURE__*/ _jsx(LoadingSpinner, {
            size: "small"
        });
    } else if (customQuantitySelection) {
        return /*#__PURE__*/ _jsx(StyledNumberInput, {
            "data-test-id": "custom-quantity-number-input",
            disabled: disabled,
            value: parseInt(value),
            onChange: function(newValue) {
                return handleChangeValue(newValue);
            },
            onClick: function(event) {
                event.preventDefault();
            }
        });
    } else {
        return /*#__PURE__*/ _jsx(StyledDropdown, {
            disabled: disabled,
            "data-test-id": dataTestId,
            options: options,
            onSelect: function(param) {
                var value = param.value;
                return handleDropdownSelect(value);
            },
            placeholder: placeholder ? placeholder : value,
            containingElement: containingElement,
            $quantityTextAlign: quantityTextAlign,
            $smallPadding: small,
            disableInput: true,
            useTabletAndAboveDropdownStyle: true
        });
    }
};
var Wrapper = styled.div.withConfig({
    componentId: "sc-a5e7f63d-0"
})([
    "min-width:",
    "px;width:",
    ";",
    ""
], function(props) {
    return props.$minWidth;
}, function(props) {
    return props.$dynamicWidth ? props.$width : "100%";
}, function(props) {
    return props.$quantityAlignRight ? "margin-left: auto;" : "";
});
var StyledDropdown = styled(Dropdown).withConfig({
    componentId: "sc-a5e7f63d-1"
})([
    "input,select{text-align:",
    ";&::placeholder{color:",
    ";}caret-color:transparent;}",
    " flex-grow:2;"
], function(props) {
    var _props_$quantityTextAlign;
    return (_props_$quantityTextAlign = props.$quantityTextAlign) !== null && _props_$quantityTextAlign !== void 0 ? _props_$quantityTextAlign : "center";
}, Core.text.default, function(props) {
    return props.$smallPadding ? css([
        "",
        "{padding:0 12px;}"
    ], InputWrapper) : "";
});
var StyledCustomDropdown = styled(Dropdown).withConfig({
    componentId: "sc-a5e7f63d-2"
})([
    "input{text-align:center;}select{&::placeholder{color:",
    ";}caret-color:transparent;}flex-grow:2;"
], Core.text.default);
/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate/Input/MeasurementInput/MeasurementInput#MeasurementInput"
 */ var StyledNumberInput = styled(MeasurementInput).withConfig({
    componentId: "sc-a5e7f63d-3"
})([
    "input{width:100%;text-align:center;padding-right:",
    "px;}"
], HORIZONTAL_PADDING);
